import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Proposal } from '../types';
import { BlockContainer } from '../BlockContainer';
import './index.scss';
import { formGroup, formControl, FormDataType, formArray } from '@core/utils/form';
import { defineBlockAdvanceForm } from '../Common/AdvanceSettings';
import { useNonNilObservable, useObservable } from '@core/utils/hooks/rxjs';
import { useConfigureBlock } from '@modules/proposal/hooks';
import { Container } from 'reactstrap';
import { FormHTMLInline } from '@core/components/form';
import { R } from '@core/utils/r';
import { useProposalContext, useProposalDataContext } from '@modules/proposal/ProposalBuilderPage';
import { EstimateService, ProposalService } from '@business/services';
import { ProposalDisplayValueEnum } from '@business/entities/order/OrderProductResponse';
import { ItemConsiderationBlockSettings } from './ItemConsiderationBlockSettings';
import { CategoryId } from '@business/entities/category';

function defineItemForm() {
  return formGroup({
    title: formControl<string>({ initialValue: 'Title' }),
    type: formControl<ProposalDisplayValueEnum>({ initialValue: ProposalDisplayValueEnum.bulletPoint }),
    quantity: formControl<number>({}),
  });
}

function defineCategoryItemForm() {
  return formGroup({
    categoryId: formControl<CategoryId>({}),
    title: formControl<string>({ initialValue: 'Category Title' }),
    items: formArray(() => defineItemForm(), { initialValue: [undefined, undefined, undefined] }),
    vccItems: formArray(() => defineItemForm(), { initialValue: [undefined, undefined, undefined] }),
  });
}

function defineCategoryImageForm() {
  return formGroup({
    categoryId: formControl<CategoryId>({}),
    imageUrl: formControl<string>({}),
    imagePosition: formControl<'left' | 'right' | 'top' | 'bottom' | 'overlay'>({ initialValue: 'left' }),
    imageWidth: formControl<string>({ initialValue: '50%' }),
    overlayTextColor: formControl<string>({ initialValue: '#ffffff' }),
    overlayBackgroundOpacity: formControl<number>({ initialValue: 0.5 }),
  });
}

function defineCategoryItemNotesForm() {
  return formGroup({
    categoryId: formControl<CategoryId>({}),
    note: formControl<string>({}),
  });
}

function defineItemConsiderationBlockForm() {
  return formGroup({
    vccHeading: formControl<string>({
      label: 'VCC Items Heading',
      initialValue: 'VCC Items',
    }),
    categories: formArray(() => defineCategoryItemForm(), { initialValue: [undefined, undefined] }),
    categoryNotes: formArray(() => defineCategoryItemNotesForm()),
    categoryImages: formArray(() => defineCategoryImageForm()), // Separate storage for category images
    advance: defineBlockAdvanceForm(),
  });
}

export type ItemConsiderationBlockForm = ReturnType<typeof defineItemConsiderationBlockForm>;
export type ItemConsiderationBlockFormData = FormDataType<typeof defineItemConsiderationBlockForm>;

export type ItemConsiderationCategoryItemNotesForm = ReturnType<typeof defineCategoryItemNotesForm>;
export type ItemConsiderationCategoryItemNotesFormData = FormDataType<typeof defineCategoryItemNotesForm>;

export type ItemConsiderationCategoryImageForm = ReturnType<typeof defineCategoryImageForm>;
export type ItemConsiderationCategoryImageFormData = FormDataType<typeof defineCategoryImageForm>;

export type ItemConsiderationCategoryItemForm = ReturnType<typeof defineCategoryItemForm>;
export type ItemConsiderationCategoryItemFormData = FormDataType<typeof defineCategoryItemForm>;



export enum ItemConsiderationBlockVariation {
  list = 'list',
}

export const ItemConsiderationBlock = memo((props: Proposal.BlockProps<ItemConsiderationBlockVariation>) => {
  const { variation, editable, preview } = props;
  const form = useMemo(defineItemConsiderationBlockForm, []);
  const formValue = useNonNilObservable(form.value$);
  const data = useProposalDataContext();

  const proposal = useProposalContext();
  const bloc = proposal?.bloc;
  const primaryColor = useMemo(() => proposal?.form.value.primaryColor, [proposal]);
  const categoryItems = useNonNilObservable(form.controls.categories.items$);

  const estimate = proposal?.estimate;
  const estimateGroupsItems = useMemo(() => estimate?.groups ?? [], [estimate]);
  const estimateGroups = useMemo(
    () => estimateGroupsItems.filter(group => group.items.filter(o => EstimateService.getItemTotal(o) > 0).length > 0),
    [estimateGroupsItems],
  );

  const categories = useObservable(bloc?.categories$, []);

  const [init, setInit] = useState(false);

  useEffect(() => {
    if (formValue.categoryNotes.length === 0) {
      form.controls.categoryNotes.push();
    }
  }, [form.controls.categoryNotes, formValue.categoryNotes.length]);

  useEffect(() => {
    if (!estimate && editable && !init) {
      form.patchValue({
        categories: [
          {
            categoryId: '1',
            title: 'Category 1',
            items: [
              {
                title: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Curabitur vitae nunc sed elit interdum tincidunt.',
                type: ProposalDisplayValueEnum.bulletPoint,
                quantity: 1,
              },
              {
                title: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Curabitur vitae nunc sed elit interdum tincidunt.',
                type: ProposalDisplayValueEnum.none,
                quantity: 1,
              },
              {
                title: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Curabitur vitae nunc sed elit interdum tincidunt.',
                type: ProposalDisplayValueEnum.quantity,
                quantity: 1,
              },
            ],
            vccItems: [],
          },
          {
            categoryId: '2',
            title: 'Category 2',
            items: [
              {
                title: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Curabitur vitae nunc sed elit interdum tincidunt.',
                type: ProposalDisplayValueEnum.quantity,
                quantity: 1,
              },
              {
                title: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Curabitur vitae nunc sed elit interdum tincidunt.',
                type: ProposalDisplayValueEnum.bulletPoint,
                quantity: 1,
              },
              {
                title: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Curabitur vitae nunc sed elit interdum tincidunt.',
                type: ProposalDisplayValueEnum.none,
                quantity: 1,
              },
            ],
            vccItems: [],
          },
        ],
      });
      setInit(true);
    }
    if (estimateGroups.length > 0) {
      form.patchValue({
        categories: estimateGroups.map(group => ({
          categoryId: group.categoryId,
          title: group.name,
          items: group.items
            .filter(item => item.weProvide && !item.isCustom)
            .map(item => ({
              title: `${item.homeOwnerDescription ?? item.title ?? 'N/A'}`,
              type: item.proposalDisplay,
              quantity: item.quantity + (item.manualQty ?? 0),
            })),
          vccItems: group.items
            .filter(item => !item.weProvide && !item.isCustom && EstimateService.getMaterialPrice(item) > 0)
            .map(item => ({
              title: `${item.homeOwnerDescription ?? item.title ?? 'N/A'}`,
              type: item.proposalDisplay,
              quantity: item.quantity + (item.manualQty ?? 0),
            })),
        })),
      });
      setInit(true);
    }
  }, [editable, init, form, estimateGroups]);

  const getCategoryNotes = useCallback(
    (categoryId: CategoryId) => R.filter(form.controls.categoryNotes.items, note => note.value.categoryId === categoryId),
    [form.controls.categoryNotes],
  );

  // Helper functions for category images
  const getCategoryImage = useCallback(
    (categoryId: CategoryId) => {
      const categoryImageItem = form.controls.categoryImages.items.find(item => item.value.categoryId === categoryId);
      return categoryImageItem
        ? categoryImageItem.value
        : {
            categoryId,
            imageUrl: '',
            imagePosition: 'left' as const,
            imageWidth: '50%',
            overlayTextColor: '#ffffff',
            overlayBackgroundOpacity: 0.5,
          };
    },
    [form.controls.categoryImages.items],
  );

  const updateCategoryImage = useCallback(
    (categoryId: CategoryId, imageData: Partial<any>) => {
      const existingIndex = form.controls.categoryImages.items.findIndex(item => item.value.categoryId === categoryId);
      if (existingIndex >= 0) {
        // Update existing
        form.controls.categoryImages.items[existingIndex].patchValue(imageData);
      } else {
        // Create new - use push() without arguments first, then patch the created control
        form.controls.categoryImages.push();
        const newIndex = form.controls.categoryImages.items.length - 1;
        form.controls.categoryImages.items[newIndex].patchValue({
          categoryId,
          imageUrl: '',
          imagePosition: 'left' as const,
          imageWidth: '50%',
          overlayTextColor: '#ffffff',
          overlayBackgroundOpacity: 0.5,
          ...imageData,
        });
      }
    },
    [form.controls.categoryImages],
  );

  useConfigureBlock({
    props,
    form,
    contentProps: { form, categories, getCategoryImage, updateCategoryImage },
    contentComponent: ItemConsiderationBlockSettings,
    onlyAdvance: true,
    modelWidth: 'lg',
  });

  return (
    <BlockContainer
      className="block-container d-flex flex-fill block-text"
      advanceForm={formValue.advance}
      excludeStyles={['borderRadius', 'borderWidth', 'borderColor', 'borderStyle', 'paddingTop', 'paddingBottom', 'paddingRight', 'paddingLeft']}
      style={{ padding: props.preview ? '30px 20px' : '0px' }}
      isSection={props.isSection}
      sectionTitle={props.sectionTitle}
      fullWidth={false}
      containerStyles={R.objectCompact(
        R.pick(formValue.advance, ['borderRadius', 'borderWidth', 'borderStyle', 'paddingTop', 'paddingBottom', 'paddingLeft', 'paddingRight']),
      )}
      editable={editable}
    >
      {/* <Container
        style={R.objectCompact(
          R.pick(formValue.advance, ['borderRadius', 'borderWidth', 'borderStyle', 'paddingTop', 'paddingBottom', 'paddingLeft', 'paddingRight']),
        )}
      > */}
      <div className="item-consideration-block">
        {categoryItems.map((category, categoryIndex) => {
          const categoryValue = category.value;
          const categoryImageData = getCategoryImage(categoryValue.categoryId);
          const hasImage = categoryImageData.imageUrl;
          const imagePosition = categoryImageData.imagePosition || 'left';

          // Render category content (title, notes, items)
          const renderCategoryContent = () => (
            <div className="category-content">
              {/* Category Title */}
              <h3 className="category-title">{categoryValue.title}</h3>

              {/* Category Notes */}
              <div className="category-notes">
                {getCategoryNotes(categoryValue.categoryId).map((ctrl, noteIndex) => {
                  const noteValue = ctrl.value;
                  if (!noteValue.note) return null;

                  return (
                    <div key={noteIndex} className="note-item mb-3">
                      <FormHTMLInline
                        control={ctrl.controls.note}
                        readonly={true}
                        transformedFieldsValue={ProposalService.getTransformedValues(data)}
                      />
                    </div>
                  );
                })}
              </div>

              {/* Category Items */}
              <div className="items">
                {category.controls.items.items.map((item, itemIndex) => {
                  const itemTitle = (
                    <FormHTMLInline
                      control={item.controls.title}
                      readonly={!editable}
                      transformedFieldsValue={ProposalService.getTransformedValues(data)}
                    />
                  );
                  return (
                    <div key={itemIndex} className="item">
                      {item.value.type === ProposalDisplayValueEnum.bulletPoint && (
                        <span className="bullet-point" style={{ backgroundColor: primaryColor }}></span>
                      )}
                      {item.value.type === ProposalDisplayValueEnum.quantity && <span className="quantity-number">{item.value.quantity}-</span>}
                      <div className="item-title">{itemTitle}</div>
                    </div>
                  );
                })}
              </div>

              {/* VCC Items */}
              {!!(category.controls.vccItems.items.length > 0) && (
                <div className="items vcc-items">
                  <FormHTMLInline control={form.controls.vccHeading} readonly={!editable} />
                  {category.controls.vccItems.items.map((item, itemIndex) => {
                    const itemTitle = (
                      <FormHTMLInline
                        control={item.controls.title}
                        readonly={!editable}
                        transformedFieldsValue={ProposalService.getTransformedValues(data)}
                      />
                    );
                    return (
                      <div key={itemIndex} className="item">
                        {item.value.type === ProposalDisplayValueEnum.bulletPoint && (
                          <span className="bullet-point" style={{ backgroundColor: primaryColor || '#45a8f7' }}></span>
                        )}
                        {item.value.type === ProposalDisplayValueEnum.quantity && (
                          <span className="quantity-number" style={{ color: primaryColor || '#45a8f7' }}>
                            {item.value.quantity}-
                          </span>
                        )}
                        <div className="item-title">{itemTitle}</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );

          // Render category image
          const renderCategoryImage = () => (
            <div className="category-image">
              <img
                src={categoryImageData.imageUrl}
                alt={`${categoryValue.title} image`}
                style={{
                  width: '100%',
                  height: 'auto',
                  borderRadius: '4px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                }}
              />
            </div>
          );

          return (
            <div key={categoryIndex} className={`category mb-4 category-${imagePosition} ${hasImage ? 'has-image' : 'no-image'}`}>
              {!hasImage ? (
                // No image - standard layout
                <div className="category-standard">{renderCategoryContent()}</div>
              ) : imagePosition === 'overlay' ? (
                // Text overlay on image
                <div className="category-overlay" style={{ position: 'relative' }}>
                  <div className="category-image-container">
                    {renderCategoryImage()}
                    <div
                      className="category-overlay-content"
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: `rgba(0, 0, 0, ${categoryImageData.overlayBackgroundOpacity || 0.5})`,
                        color: categoryImageData.overlayTextColor || '#ffffff',
                        padding: '20px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        borderRadius: '4px',
                      }}
                    >
                      {renderCategoryContent()}
                    </div>
                  </div>
                </div>
              ) : imagePosition === 'top' ? (
                // Image on top, content below
                <div className="category-top">
                  <div className="category-image-container mb-3">{renderCategoryImage()}</div>
                  {renderCategoryContent()}
                </div>
              ) : imagePosition === 'bottom' ? (
                // Content on top, image below
                <div className="category-bottom">
                  {renderCategoryContent()}
                  <div className="category-image-container mt-3">{renderCategoryImage()}</div>
                </div>
              ) : (
                // Left/Right positioning
                <div className="category-horizontal">
                  <div
                    className="category-horizontal-content"
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      flexDirection: imagePosition === 'right' ? 'row' : 'row',
                      gap: '20px',
                    }}
                  >
                    <div
                      className="category-text-section"
                      style={{
                        flex: imagePosition === 'right' ? '1' : `0 0 calc(100% - ${categoryImageData.imageWidth})`,
                        order: imagePosition === 'right' ? 1 : 2,
                      }}
                    >
                      {renderCategoryContent()}
                    </div>
                    <div
                      className="category-image-section"
                      style={{
                        flex: `0 0 ${categoryImageData.imageWidth}`,
                        order: imagePosition === 'right' ? 2 : 1,
                      }}
                    >
                      {renderCategoryImage()}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {/* </Container> */}
    </BlockContainer>
  );
});
